import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import useAuth from "../../../../hooks/useAuth";
import labels from "../../../../constants/displayFields";
import BaseInfoModal from "./BaseInfoModal";
import QuantityRow from "./QuantityRow";
import useModalStates from "./hooks/useModalStates";
import WarehouseQuantityInfoModal from "./WarehouseQuantityInfoModal";
import AllocatedQuantityInfoModal from "./AllocatedQuantityInfoModal";
import ReservedQuantityInfoModal from "./ReservedQuantityInfoModal";
import InCartQuantityInfoModal from "./InCartQuantityInfoModal";
import RecalculateAvailableQuantityButton from "./RecalculateAvailableQuantityButton";

export default function QuantityCalculationModal({ open, asset, onClose }) {
  const { user } = useAuth();
  const { settings } = user.company;
  const quantityLabel = settings.quantity_label || "Quantity";
  const modalStates = useModalStates(["warehouse", "allocated", "reserved", "inCart"]);
  const isAdminOrManager = ["admin", "manager"].includes(user.role);
  const reservedQuantity = Number(asset?.reservedQuantity || 0);
  const canRecalculate = asset?.type === "Kit";

  return (
    <BaseInfoModal
      open={open}
      onClose={onClose}
      title={`${quantityLabel} Calculation`}
      maxWidth="xs"
      titleAction={canRecalculate ? <RecalculateAvailableQuantityButton asset={asset} /> : null}
    >
      <Box variant="body2" color="text.secondary" sx={{ mb: 3, px: 3, textAlign: "center" }}>
        This screen shows you how {quantityLabel.toLowerCase()} was calculated for this asset
      </Box>
      <Box display="flex" flexDirection="column" gap={2}>
        <QuantityRow
          value={asset.warehouseQuantity}
          label={labels.warehouse_quantity}
          onInfoClick={modalStates.warehouse.open}
          showInfoButton={isAdminOrManager}
        />
        <QuantityRow
          value={asset.inCartQuantity}
          label={labels.in_cart_quantity}
          operator="minus"
          onInfoClick={modalStates.inCart.open}
          showInfoButton={isAdminOrManager}
        />
        {settings.manual_location_checkout && (
          <QuantityRow
            value={asset.allocatedQuantity}
            label={labels.allocated_quantity}
            operator="minus"
            onInfoClick={modalStates.allocated.open}
            showInfoButton={isAdminOrManager}
          />
        )}
        {settings.reservations && reservedQuantity !== 0 && (
          <QuantityRow
            value={reservedQuantity}
            label={labels.reserved_quantity}
            operator="minus"
            onInfoClick={modalStates.reserved.open}
            showInfoButton={isAdminOrManager}
          />
        )}
        <Divider sx={{ borderColor: "rgba(0, 0, 0, 0.5)" }} />
        <QuantityRow
          value={asset.availableQuantity}
          label={labels.available_quantity}
          operator="equals"
          showInfoButton={false}
        />
      </Box>

      {modalStates.warehouse.isOpen && (
        <WarehouseQuantityInfoModal asset={asset} onClose={modalStates.warehouse.close} />
      )}
      {modalStates.allocated.isOpen && (
        <AllocatedQuantityInfoModal asset={asset} onClose={modalStates.allocated.close} />
      )}
      {modalStates.reserved.isOpen && (
        <ReservedQuantityInfoModal asset={asset} onClose={modalStates.reserved.close} />
      )}
      {modalStates.inCart.isOpen && (
        <InCartQuantityInfoModal asset={asset} onClose={modalStates.inCart.close} />
      )}
    </BaseInfoModal>
  );
}
